import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import Button from '@renderer/components/bahela/Button'
import { FaVideo } from 'react-icons/fa'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { formatAmount, normalizeCurrency } from '@renderer/utils/formatCurrency'

// Import Bahela assets
import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaRed from '/assets/bahela/bahela-red.webp'
import printInfoId from '/assets/bahela/print-info-id.webp'
import printInfoEn from '/assets/bahela/print-info-en.webp'
import backButtonRed from '/assets/bahela/back-button-red.svg'
import arrow from '/assets/bahela/arrow.svg'
import twoPhotos from '/assets/bahela/2-photos.webp'
import fourPhotos from '/assets/bahela/2-photos.webp'

const CheckoutEuphoria: React.FC = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, transaction, logActivity, userPreferences } = useSessionStateStore()
  const customFrameSur = userPreferences.customFrameSurchargePerPackage ?? 0
  const { config: globalConfig } = useConfigStore()
  const config = globalConfig?.pages.checkout
  const projectCurrency = normalizeCurrency(globalConfig?.resolvedCurrency)

  // States
  const [selectedPrintIndex, setSelectedPrintIndex] = useState(0)
  const [remainingTime, setRemainingTime] = useState('01:00')

  // Initialize countdown hook
  const checkoutCountdown = useCountdown(config?.settings?.checkoutTimeout || 60)

  // Get or create print packages
  const printPackages = [
    {
      id: '2 Strips',
      quantity: 1,
      price:
        config?.settings.packages?.find((p) => p.type === 'print' && p.quantity === 1)?.price || 0,
      illustrationImgUrl: twoPhotos
    },
    {
      id: '4 Strips',
      quantity: 2,
      price:
        config?.settings.packages?.find((p) => p.type === 'print' && p.quantity === 2)?.price || 0,
      illustrationImgUrl: fourPhotos
    }
  ]

  // Get or create recording package
  const recordingPackage = config?.settings.packages?.find((p) => p.type === 'recording') || {
    type: 'recording',
    price: 0,
    optional: false
  }

  // Initialize countdown
  useEffect(() => {
    // Set up countdown callbacks
    checkoutCountdown.setOnTick(() => {
      setRemainingTime(checkoutCountdown.getFormattedRemainingSeconds())
    })

    checkoutCountdown.setOnComplete(() => {
      continueToPayment()
    })

    checkoutCountdown.start()

    return (): void => {
      checkoutCountdown.stop()
    }
  }, [])

  const calculateDiscount = (basePrice: number): number => {
    const discountValue = transaction.discount || 0
    if (discountValue < 0) {
      // Negative value means percentage discount
      return Math.floor((basePrice * Math.abs(discountValue)) / 100)
    }
    // Positive value means fixed amount discount
    return discountValue
  }

  const getBasePrice = (packagePrice: number): number => {
    return packagePrice + recordingPackage.price + customFrameSur
  }

  const calculateTotal = (packagePrice: number): number => {
    const basePrice = getBasePrice(packagePrice)
    const discount = calculateDiscount(basePrice)
    return Math.max(0, basePrice - discount)
  }

  const continueToPayment = async (): Promise<void> => {
    const selectedPrint = printPackages[selectedPrintIndex]
    const basePrice = getBasePrice(selectedPrint.price)
    const finalDiscount = calculateDiscount(basePrice)

    setSessionState({
      userPreferences: {
        copies: selectedPrint.quantity,
        useRecording: true
      },
      transaction: {
        amount: calculateTotal(selectedPrint.price),
        discount: finalDiscount,
        selectedPackages: [
          {
            type: 'print',
            quantity: selectedPrint.quantity,
            price: selectedPrint.price
          },
          recordingPackage
        ]
      }
    })

    // Log checkout selection activity
    logActivity('Event checkout selection completed', {
      copies: selectedPrint.quantity,
      amount: calculateTotal(selectedPrint.price),
      discount: finalDiscount,
      useRecording: true
    })
    if (calculateTotal(selectedPrint.price) > 0) {
      navigate(`/${projectId}/payment`)
    } else {
      navigate(`/${projectId}/capture`)
    }
  }

  const moveLeft = (): void => {
    setSelectedPrintIndex((selectedPrintIndex - 1 + printPackages.length) % printPackages.length)
  }

  const moveRight = (): void => {
    setSelectedPrintIndex((selectedPrintIndex + 1) % printPackages.length)
  }

  // Call all hooks at the top level
  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaRed = useCachedImage(bahelaRed)
  const cachedPrintInfo = useCachedImage(i18n.language === 'id' ? printInfoId : printInfoEn)
  const cachedBackButtonRed = useCachedImage(backButtonRed)
  const cachedArrow = useCachedImage(arrow)

  return (
    <div className="background relative bg-romantic w-full h-full overflow-hidden">
      <div
        className="deco-img absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />
      <img
        src={cachedBahelaRed}
        alt="Bahela Logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
      <img
        src={cachedPrintInfo}
        alt="Print Information"
        className="absolute z-[2] right-2 top-1/2 -translate-y-1/2 w-[543px] h-[393px]"
      />
      <div className="absolute z-[5] left-[50px] top-[102px] flex flex-col items-center">
        <button onClick={() => navigate(`/${projectId}/filter`)}>
          <img
            src={cachedBackButtonRed}
            className="w-[106px] h-[106px] btn-shadow"
            alt={t('common.back')}
          />
        </button>
        <span className="font-body-bahela text-[32px] text-methadone">{t('common.back')}</span>
      </div>
      <h1 className="font-serif-bahela z-[4] text-white drop-shadow-2xl absolute left-[111px] top-1/2 -translate-y-1/2 text-[100px] leading-none flex flex-col">
        {i18n.language === 'id' ? 'Mau ' : 'Printing '}
        <br />
        {i18n.language === 'id' ? 'Cetak ' : 'Options'}
        <br />
        {i18n.language === 'id' ? 'Berapa?' : ''}
      </h1>
      <div className="absolute z-[4] left-1/2 -translate-x-1/2 h-full w-auto gap-16 flex justify-center">
        <div className="h-full w-[93px] relative">
          <button className="absolute top-[187px] left-0 btn-shadow" onClick={moveLeft}>
            <img
              src={cachedArrow}
              className="h-[135px] w-[93px] object-contain drop-shadow-2xl"
              alt="Left Arrow"
            />
          </button>
        </div>
        <div className="h-full flex gap-2">
          {printPackages.map((print, index) => (
            <div
              key={print.id}
              className={
                'h-full p-[43px] flex flex-col justify-between items-center gap-[20px] ' +
                (index === 0 ? 'bg-hibiscus' : 'bg-prominentBlue')
              }
            >
              <div
                style={{
                  boxShadow: index === selectedPrintIndex ? '0 0 10px 5px rgba(0, 0, 0, 0.5)' : '',
                  transition: 'background-color 0.2s ease-out, box-shadow 0.2s ease-in-out'
                }}
                onClick={() => setSelectedPrintIndex(index)}
                className={
                  'flex flex-col cursor-pointer items-center rounded-3xl gap-[33px] p-[43px] mb-[20px] ' +
                  (index === selectedPrintIndex
                    ? 'bg-uriYellow box-shadow-active'
                    : ' drop-shadow-md hover:shadow-lg')
                }
              >
                <img
                  className="w-auto h-[523px] object-contain"
                  src={useCachedImage(print.illustrationImgUrl)}
                  alt={print.id}
                />
                <h2
                  className={
                    'font-serif-bahela text-[42px] ' +
                    (index === selectedPrintIndex
                      ? index === 0
                        ? 'text-hibiscus'
                        : 'text-prominentBlue'
                      : 'text-uriYellow')
                  }
                >
                  {print.id}
                </h2>
              </div>
              <div className={'text-center'}>
                <div className="w-[320px]">
                  <h2
                    className={`font-serif-bahela text-white whitespace-nowrap ${transaction.discount ? 'line-through decoration-methadone text-[35px]' : 'text-[70px]'}`}
                  >
                    {getBasePrice(print.price) === 0
                      ? 'Free'
                      : formatAmount(getBasePrice(print.price), projectCurrency)}
                  </h2>
                  {transaction.discount > 0 && (
                    <h2 className="font-serif-bahela text-white text-[70px] whitespace-nowrap">
                      {formatAmount(calculateTotal(print.price), projectCurrency)}
                    </h2>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="h-full w-[93px] relative">
          <button
            type={'button'}
            className="absolute top-[187px] left-0 btn-shadow"
            onClick={moveRight}
          >
            <img
              src={cachedArrow}
              className="h-[135px] w-[93px] object-contain rotate-180 drop-shadow-2xl"
              alt="Right Arrow"
            />
          </button>
        </div>
        <div className="absolute bottom-[220px] left-1/2 -translate-x-1/2 z-[5] flex items-center">
          <div
            className={`
              flex items-center gap-4 px-8 py-4 
              rounded-full
              bg-uriYellow shadow-lg
            `}
          >
            <div className="flex items-center gap-3">
              <FaVideo size="24" color="#B3336C" />
              <div className="flex items-center gap-3 mr-12">
                <span className="font-serif-bahela text-[28px] text-hibiscus">
                  {i18n.language === 'id' ? 'Rekam dan Foto' : 'Record and Shoot'}
                </span>
                <span className="font-serif-bahela text-[24px] text-hibiscus opacity-75">
                  (Free)
                </span>
              </div>
              <div
                className={`
                  w-14 h-8 rounded-full p-1
                  bg-hibiscus
                `}
              >
                <div
                  className={`
                    w-6 h-6 rounded-full
                    bg-uriYellow shadow-md
                    translate-x-6
                  `}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="absolute z-[6] right-[127px] bottom-[120px] drop-shadow-2xl flex flex-col items-center">
        <span className="text-[50px] text-white font-serif-bahela">{remainingTime}</span>
        <Button
          onClick={continueToPayment}
          className="text-[50px] bottom-[190px]"
          textColor="methadone"
        >
          {calculateTotal(printPackages[selectedPrintIndex].price) === 0
            ? i18n.language === 'id'
              ? 'Lanjutkan'
              : 'Continue'
            : i18n.language === 'id'
              ? 'Bayar'
              : 'Pay'}
        </Button>
      </div>
    </div>
  )
}

export default CheckoutEuphoria
